<script>
	import { onMount, afterUpdate } from 'svelte';
	import { marked } from 'marked';
	import { API_BASE_URL } from '$lib/config/api.js';

	let faqs = [];
	let isLoading = true;
	let error = '';
	let openId = null;
	let selectedCategory = null;
	let stickyEl;
	let stickyHeight = 0;

	function updateStickyHeight() {
		if (stickyEl) stickyHeight = stickyEl.offsetHeight;
	}

	$: categories = [...new Set(faqs.map((f) => f.category).filter(Boolean))];
	$: filteredFaqs = selectedCategory
		? faqs.filter((f) => f.category === selectedCategory)
		: faqs;

	$: categoryDisplayMap = Object.fromEntries(
		faqs.filter((f) => f.category).map((f) => [f.category, f.category_display || f.category])
	);

	onMount(async () => {
		await fetchFaqs();
	});

	afterUpdate(() => {
		updateStickyHeight();
	});

	async function fetchFaqs() {
		isLoading = true;
		error = '';

		try {
			const response = await fetch(`${API_BASE_URL}/api/faqs/`);
			if (response.ok) {
				const result = await response.json();
				faqs = result.data || [];
			} else {
				error = 'FAQ를 불러오는데 실패했습니다.';
			}
		} catch (e) {
			error = 'FAQ를 불러오는데 실패했습니다.';
		}

		isLoading = false;
	}

	function toggle(id) {
		openId = openId === id ? null : id;
	}
</script>

<svelte:head>
	<title>FAQ - 단비데스크</title>
</svelte:head>

<!-- 타이틀 + 카테고리 고정 영역 -->
<div bind:this={stickyEl} class="fixed top-16 left-0 right-0 z-40 bg-gradient-to-br from-blue-50 via-white to-indigo-50">
	<div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
		<div class="text-center pt-8 pb-4">
			<h1 class="text-3xl font-bold text-gray-900 mb-2">자주 묻는 질문</h1>
			<p class="text-gray-600">궁금하신 점을 확인해보세요.</p>
		</div>

		{#if !isLoading && !error && faqs.length > 0 && categories.length > 1}
			<div class="flex flex-wrap gap-2 pb-4">
				<button
					on:click={() => (selectedCategory = null)}
					class="px-4 py-1.5 rounded-full text-sm font-medium transition-colors {selectedCategory === null
						? 'bg-blue-600 text-white'
						: 'bg-white text-gray-600 border border-gray-200 hover'}"
				>
					전체
				</button>
				{#each categories as category}
					<button
						on:click={() => (selectedCategory = category)}
						class="px-4 py-1.5 rounded-full text-sm font-medium transition-colors {selectedCategory === category
							? 'bg-blue-600 text-white'
							: 'bg-white text-gray-600 border border-gray-200 hover'}"
					>
						{categoryDisplayMap[category] || category}
					</button>
				{/each}
			</div>
		{/if}
	</div>
	<div class="border-b border-gray-200/60"></div>
</div>

<!-- 콘텐츠 영역 -->
<div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-12" style="padding-top: {stickyHeight + 64 + 16}px">
	{#if isLoading}
		<div class="flex items-center justify-center py-12">
			<svg class="animate-spin h-8 w-8 text-blue-600" viewBox="0 0 24 24">
				<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
				<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
			</svg>
		</div>
	{:else if error}
		<div class="text-center py-12">
			<p class="text-red-600 mb-4">{error}</p>
			<button
				class="px-4 py-2 bg-blue-600 hover text-white rounded-lg"
				on:click={fetchFaqs}
			>
				다시 시도
			</button>
		</div>
	{:else if faqs.length === 0}
		<div class="text-center py-12">
			<p class="text-gray-500">등록된 FAQ가 없습니다.</p>
		</div>
	{:else}
		<div class="space-y-3">
			{#each filteredFaqs as faq}
				<div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
					<button
						on:click={() => toggle(faq.id)}
						class="w-full flex items-center justify-between px-6 py-4 text-left hover transition-colors"
					>
						<span class="font-medium text-gray-900">{faq.question}</span>
						<svg
							class="w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ml-4 {openId === faq.id ? 'rotate-180' : ''}"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
						</svg>
					</button>
					{#if openId === faq.id}
						<div class="faq-answer px-6 pb-5 border-t border-gray-100 pt-4">
							{@html marked(faq.answer || '')}
						</div>
					{/if}
				</div>
			{/each}
		</div>
	{/if}
</div>

<style>
	:global(.faq-answer) {
		color: #374151;
		font-size: 0.9375rem;
		line-height: 1.7;
	}

	:global(.faq-answer h2) {
		font-size: 1rem;
		font-weight: 700;
		color: #111827;
		margin-top: 1.25rem;
		margin-bottom: 0.5rem;
	}

	:global(.faq-answer h3) {
		font-size: 0.9375rem;
		font-weight: 600;
		color: #1f2937;
		margin-top: 1rem;
		margin-bottom: 0.4rem;
	}

	:global(.faq-answer p) {
		margin-bottom: 0.75rem;
	}

	:global(.faq-answer ol),
	:global(.faq-answer ul) {
		margin-top: 0.25rem;
		margin-bottom: 0.75rem;
		padding-left: 1.25rem;
	}

	:global(.faq-answer ol) {
		list-style-type: decimal;
	}

	:global(.faq-answer ul) {
		list-style-type: disc;
	}

	:global(.faq-answer li) {
		margin-bottom: 0.25rem;
	}

	:global(.faq-answer strong) {
		font-weight: 600;
		color: #111827;
	}

	:global(.faq-answer blockquote) {
		border-left: 3px solid #3b82f6;
		background-color: #eff6ff;
		padding: 0.75rem 1rem;
		margin: 0.75rem 0;
		border-radius: 0 0.5rem 0.5rem 0;
		color: #1e40af;
	}

	:global(.faq-answer blockquote p) {
		margin-bottom: 0;
	}

	:global(.faq-answer table) {
		width: 100%;
		border-collapse: collapse;
		margin: 0.75rem 0;
		font-size: 0.875rem;
	}

	:global(.faq-answer th) {
		background-color: #f3f4f6;
		font-weight: 600;
		text-align: left;
		padding: 0.5rem 0.75rem;
		border-bottom: 2px solid #e5e7eb;
		color: #374151;
	}

	:global(.faq-answer td) {
		padding: 0.5rem 0.75rem;
		border-bottom: 1px solid #f3f4f6;
	}

	:global(.faq-answer tr:hover td) {
		background-color: #f9fafb;
	}

	:global(.faq-answer code) {
		background-color: #f3f4f6;
		padding: 0.125rem 0.375rem;
		border-radius: 0.25rem;
		font-size: 0.8125rem;
		color: #dc2626;
	}

	:global(.faq-answer a) {
		color: #2563eb;
		text-decoration: underline;
		text-underline-offset: 2px;
	}

	:global(.faq-answer a:hover) {
		color: #1d4ed8;
	}

	:global(.faq-answer hr) {
		border: none;
		border-top: 1px solid #e5e7eb;
		margin: 1rem 0;
	}
</style>